import type { DomainConfig } from './config.ts'

export interface ViewerConfig {
  locations: { id: string; view?: 'quad' | 'audio' }[]
  defaultPreset: string
  presets: Record<string, ViewerPreset>
}

export interface ViewerPreset {
  /** Explicit selection in picker/default-camera order. */
  includeLocations?: string[]
  /** All API locations except these, preserving API order. */
  excludeLocations?: string[]
}

export function validatePresets(presets: unknown): void {
  if (!presets || typeof presets !== 'object' || Array.isArray(presets))
    throw new Error('presets must be an object')
  for (const [name, value] of Object.entries(presets)) {
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(name))
      throw new Error(`Invalid preset name: ${name}`)
    if (!value || typeof value !== 'object' || Array.isArray(value))
      throw new Error(`Preset ${name} must be an object`)
    const preset = value as Record<string, unknown>
    if (preset.includeLocations !== undefined && preset.excludeLocations !== undefined)
      throw new Error(`Preset ${name} cannot include and exclude locations`)
    for (const [key, ids] of Object.entries(preset)) {
      if (key !== 'includeLocations' && key !== 'excludeLocations')
        throw new Error(`Unknown preset option: ${name}.${key}`)
      if (
        !Array.isArray(ids) ||
        ids.some((id) => typeof id !== 'string' || !id.trim()) ||
        new Set(ids).size !== ids.length
      )
        throw new Error(`Preset ${name} ${key} must be an array of unique location IDs`)
    }
  }
}

export function parseViewerConfig(input: unknown): ViewerConfig {
  if (!input || typeof input !== 'object' || Array.isArray(input))
    throw new Error('Viewer config must be an object')
  const value = input as Record<string, unknown>
  if (!Array.isArray(value.locations))
    throw new Error('Viewer locations must be an array')
  const ids = new Set<string>()
  for (const location of value.locations) {
    if (
      !location ||
      typeof location !== 'object' ||
      Array.isArray(location) ||
      typeof location.id !== 'string' ||
      !location.id ||
      ids.has(location.id)
    )
      throw new Error('Invalid or duplicate viewer location')
    if (
      location.view !== undefined &&
      location.view !== 'quad' &&
      location.view !== 'audio'
    )
      throw new Error(`Invalid view for ${location.id}`)
    ids.add(location.id)
  }
  if ('routes' in value)
    throw new Error('Replace viewer routes with presets and defaultPreset')
  validatePresets(value.presets)
  if (
    typeof value.defaultPreset !== 'string' ||
    !Object.hasOwn(value.presets as object, value.defaultPreset)
  )
    throw new Error('defaultPreset must name a configured preset')
  return structuredClone(value) as unknown as ViewerConfig
}

export function validateViewerReferences(
  viewer: ViewerConfig,
  domain: DomainConfig,
): void {
  const ids = new Set(domain.locations.map((location) => location.id))
  for (const id of [
    ...viewer.locations.map((location) => location.id),
    ...Object.values(viewer.presets).flatMap(
      (preset) => preset.includeLocations ?? preset.excludeLocations ?? [],
    ),
  ]) {
    if (!ids.has(id)) throw new Error(`Viewer references unknown location ${id}`)
  }
}
